import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns'

import { enUS as en, fr } from 'date-fns/locale'
import { capitalize, isNil, isString } from 'lodash'

import type { PaymentTermOption } from '@/shared/models/companyModel'

export type LocalizeMillisecondsToDaysProps = {
    distance: number
    locale?: string
}

export const localizeMillisecondsToDays = ({ distance, locale = 'en' }: LocalizeMillisecondsToDaysProps): string => {
    return capitalize(
        formatDistanceToNowStrict(new Date(Date.now() + distance), {
            addSuffix: true,
            locale: locale === 'fr' ? fr : en,
            unit: 'day'
        })
    )
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T/

const isDateOnlyString = (value: unknown): value is string => isString(value) && DATE_ONLY_PATTERN.test(value)

/**
 * Reads a date the API may send as a calendar date (`yyyy-MM-dd`) or as a date-time.
 * A calendar date is built at local midnight of that day, so no time zone moves it to another day.
 * A date-time is the moment it says, shown on the day it falls on in the local time zone.
 */
export const parseCalendarDate = (date: number | Date | string): Date => {
    if (isDateOnlyString(date)) return parseISO(date)

    return new Date(date)
}

export type LocalizeDateOptions = {
    format?: string
    hasHours?: boolean
}

export const localizeDate = (
    date: number | Date | string | undefined,
    options?: { locale?: string; dateFormatOptions?: LocalizeDateOptions }
): string | undefined => {
    if (isNil(date)) return undefined

    const locale = options?.locale || 'en'
    let dateFormatOptions = options?.dateFormatOptions || {}

    if (locale === 'fr') {
        dateFormatOptions = {
            ...dateFormatOptions,
            format: 'dd/MM/yyyy'
        }
    }

    let dateFormat = dateFormatOptions.format || 'yyyy-MM-dd'

    if (dateFormatOptions.hasHours) {
        dateFormat = `${dateFormat}, HH:mm:ss`
    }

    return format(parseCalendarDate(date), dateFormat)
}

export const dateToISOStringWithTimeZoneOffset = (date: Date): string => {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()
}

export const isISODateString = (dateString: string) => {
    try {
        const parsedDate = parseISO(dateString)
        if (isDateOnlyString(dateString)) return isValid(parsedDate)

        return isValid(parsedDate) && DATE_TIME_PATTERN.test(dateString)
    } catch {
        return false
    }
}

export const calculateDueDate = (
    invoiceDate: Date | string,
    paymentTermType: PaymentTermOption,
    repaymentPeriod: number
): Date => {
    const dueDate = parseCalendarDate(invoiceDate)

    if (paymentTermType === 'net') {
        dueDate.setDate(dueDate.getDate() + repaymentPeriod)
    } else if (paymentTermType === 'eom') {
        dueDate.setMonth(dueDate.getMonth() + 1, 0)
        dueDate.setDate(dueDate.getDate() + repaymentPeriod)
    } else if (paymentTermType === 'eomf') {
        dueDate.setMonth(dueDate.getMonth() + 2, 0)
        dueDate.setDate(dueDate.getDate() + repaymentPeriod)
    }

    return dueDate
}
